import { get, put } from "@/services/axiosUitils";

export interface UserPreferences {
  height_unit: "cm" | "ft_in";
  weight_unit: "kg" | "lbs";
  distance_unit: "km" | "mi";
  temperature_unit: "c" | "f";
}

class PreferencesService {
  private static instance: PreferencesService;

  private constructor() { }

  public static getInstance(): PreferencesService {
    if (!PreferencesService.instance) {
      PreferencesService.instance = new PreferencesService();
    }
    return PreferencesService.instance;
  }

  // Get user preferences from API
  async getUserPreferences(): Promise<UserPreferences> {
    try {
      // First try to get from API (GET request)
      const response = await get<any>("/food_scanner/preferences/");

      if (response) {
        // Handle different response formats
        let prefs: UserPreferences;

        // If response has success wrapper, extract the data
        if (response.success || response.height_unit !== undefined) {
          prefs = {
            height_unit: response.height_unit ?? "cm",
            weight_unit: response.weight_unit ?? "lbs",
            distance_unit: response.distance_unit ?? "km",
            temperature_unit: response.temperature_unit ?? "c",
          };
        } else {
          // Direct response format
          prefs = response;
        }

        // Store in localStorage for offline access
        localStorage.setItem("user_preferences", JSON.stringify(prefs));
        return prefs;
      }
    } catch (error: any) {
      // If 404 or preferences don't exist, create default ones
      if (error?.response?.status === 404) {
        const defaultPrefs: UserPreferences = {
          height_unit: "cm",
          weight_unit: "lbs",
          distance_unit: "km",
          temperature_unit: "c",
        };

        // Try to create default preferences
        await this.updateUserPreferences(defaultPrefs);
        return defaultPrefs;
      }
    }

    // Fallback to localStorage
    const cachedPrefs = localStorage.getItem("user_preferences");
    if (cachedPrefs) {
      return JSON.parse(cachedPrefs);
    }

    // Default preferences
    return {
      height_unit: "cm",
      weight_unit: "lbs",
      distance_unit: "km",
      temperature_unit: "c",
    };
  }

  // Update user preferences
  async updateUserPreferences(preferences: UserPreferences): Promise<boolean> {
    try {
      const response = await put<any>(
        "/food_scanner/preferences/",
        preferences
      );

      if (response) {
        // Update localStorage
        localStorage.setItem("user_preferences", JSON.stringify(preferences));
        return true;
      }
      return false;
    } catch (_error) {
      return false;
    }
  }
}

export default PreferencesService.getInstance();
